import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from "react-leaflet";
import React, { useEffect, useState } from "react";
import "leaflet/dist/leaflet.css";
import icon from "../assets/dropPin.png";
import L from "leaflet";
import { useNavigate } from "react-router-dom";
import { Box, Link, Typography, Button } from "@mui/material";
import GppGoodIcon from "@mui/icons-material/GppGood";
import PropTypes from "prop-types";
import queryString from "query-string";
import './Search.css';

// Haversine formula to calculate distance in miles
const haversineMiles = (lat1, lon1, lat2, lon2) => {
  const R = 3959;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

function MapView({ center, zoom, visible, onMapReady, circle, bounds }) {
  const map = useMap();

  useEffect(() => {
    onMapReady(map);
  }, [map, onMapReady]);

  useEffect(() => {
    if (visible) map.invalidateSize();
  }, [visible, map]);

  useEffect(() => {
    if (bounds && bounds.minLatitude != null && bounds.maxLatitude != null && bounds.minLongitude != null && bounds.maxLongitude != null) {
      // Area mode: fit the searched rectangle. When the search was
      // triggered from the map button this is the current viewport,
      // so the user's zoom level is preserved.
      map.fitBounds([[bounds.minLatitude, bounds.minLongitude], [bounds.maxLatitude, bounds.maxLongitude]]);
    } else {
      map.setView([center.latitude, center.longitude], zoom || 12);
    }
  }, [map, center.latitude, center.longitude, zoom, bounds?.minLatitude, bounds?.maxLatitude, bounds?.minLongitude, bounds?.maxLongitude]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    map.eachLayer((layer) => {
      if (layer['_latlng'] !== undefined && layer['_mRadius'] !== undefined) {
        layer.remove();
      }
    });
    if (circle !== undefined)
      L.circle([center.latitude, center.longitude], { radius: circle * 1609 }).addTo(map);
  }, [map, center.latitude, center.longitude, circle]);

  return null;
}

function MapEvents({ setShowSearchArea, searchCenter, searchDistance, isGeoMode }) {
  const map = useMapEvents({
    moveend: () => {
      handleMapEvent();
    },
    zoomend: () => {
      handleMapEvent();
    }
  });

  function handleMapEvent() {
    if (!isGeoMode || !searchCenter) {
      setShowSearchArea(false);
      return;
    }
    const moved = haversineMiles(
      searchCenter.latitude,
      searchCenter.longitude,
      map.getCenter().lat,
      map.getCenter().lng
    );
    const threshold = Math.max(0.5, (Number(searchDistance) || 0) * 0.1);
    setShowSearchArea(moved > threshold);
  }

  return null;
}

function CompactPopup({ place }) {
    const navigate = useNavigate();

    const handleViewDetails = () => {
        if (place.friendlyUrl) {
            navigate("/" + encodeURIComponent(place.friendlyUrl));
        } else {
            navigate("/details?id=" + place.id);
        }
    };

    const servicesText = Array.isArray(place.servicesProvided)
        ? place.servicesProvided.join(" | ")
        : place.servicesProvided || "";

    const description = servicesText || place.shortDescription || "";

    return (
        <Box sx={{ padding: "8px", minWidth: "220px", fontSize: "0.9rem" }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: "4px", marginBottom: "4px" }}>
                <Link
                    component="span"
                    underline="hover"
                    onClick={handleViewDetails}
                    sx={{ cursor: "pointer", fontWeight: "bold", flex: 1, fontSize: "0.95rem" }}
                >
                    {place.name}
                </Link>
                {place.isMemberProvider && (
                    <GppGoodIcon sx={{ fontSize: "1em", color: "primary.main", flexShrink: 0 }} />
                )}
            </Box>

            {place.rating !== undefined && (
                <Box sx={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "4px" }}>
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: "500" }}>
                        {place.rating.toFixed(1)}
                    </Typography>
                    <div className="stars-outer">
                        <div className="stars-inner"
                            style={{ width: (Math.round((place.rating / 5) * 67)) }}></div>
                    </div>
                </Box>
            )}

            {(place.city || place.state) && (
                <Typography sx={{ fontSize: "0.8rem", marginBottom: "4px", fontStyle: "italic", color: "#666" }}>
                    {place.city}{place.city && place.state ? ", " : ""}{place.state}
                </Typography>
            )}

            {description && (
                <Typography
                    sx={{
                        fontSize: "0.8rem",
                        marginBottom: "6px",
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 2,
                        overflow: "hidden",
                        color: "#555"
                    }}
                >
                    {description}
                </Typography>
            )}

            <Link
                component="span"
                underline="hover"
                onClick={handleViewDetails}
                sx={{ cursor: "pointer", fontSize: "0.85rem", fontWeight: "500" }}
            >
                View Details →
            </Link>
        </Box>
    );
}

export default function Map({
    places = [],
    zoom = 0,
    center = { latitude: 0, longitude: 0 },
    circle = undefined,
    showPopup = true,
    visible = true,
    searchCenter = null,
    searchDistance = 0,
    isGeoMode = false,
    bounds = null
}) {
    const [showSearchArea, setShowSearchArea] = useState(false);
    const [mapInstance, setMapInstance] = useState(null);
    const navigate = useNavigate();
    const customIcon = new L.Icon({
        iconUrl: icon,
        iconSize: [25, 35],
        iconAnchor: [5, 30]
    });

    const handleSearchThisArea = () => {
        if (!mapInstance) return;
        const parsed = queryString.parse(window.location.search);
        const bounds = mapInstance.getBounds();
        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();
        navigate(`?minLatitude=${sw.lat}&maxLatitude=${ne.lat}&minLongitude=${sw.lng}&maxLongitude=${ne.lng}&category=${parsed.category || ""}&availability=${parsed.availability || ""}&kidstoggle=${parsed.kidstoggle || false}&page=1&view=${parsed.view || "list"}`, { replace: true });
        setShowSearchArea(false);
    };

    return (
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            <MapContainer
                classsName="map"
                //center={[center.latitude, center.longitude]}
                zoom={12}
                scrollWheelZoom={true}
                style={{width:"100%",height:"100%"}}
            >
                <TileLayer
                    attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a>
            contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {places?.map((place)=>{
                    return(
                        <Marker key={place.id || place.name} icon={customIcon} position={[place.latitude, place.longitude]}>
                            {showPopup && (
                                <Popup>
                                    <CompactPopup place={place} />
                                </Popup>
                            )}
                        </Marker>
                    );
                })}

                <MapView center={center} zoom={zoom} visible={visible} onMapReady={setMapInstance} circle={circle} bounds={bounds} />
                <MapEvents setShowSearchArea={setShowSearchArea} searchCenter={searchCenter} searchDistance={searchDistance} isGeoMode={isGeoMode} />
            </MapContainer>

            {showSearchArea && isGeoMode && (
                <Box sx={{ position: 'absolute', top: '12px', left: '50%', transform: 'translateX(-50%)', zIndex: 1000 }}>
                    <Button variant="contained" size="small" onClick={handleSearchThisArea} sx={{ borderRadius: '20px', textTransform: 'none', boxShadow: 3 }}>
                        Search This Area
                    </Button>
                </Box>
            )}
        </Box>
    );
}

Map.propTypes = {
    places: PropTypes.array,
    zoom: PropTypes.number,
    center: PropTypes.shape({
        latitude: PropTypes.number,
        longitude: PropTypes.number
    }),
    circle: PropTypes.number,
    showPopup: PropTypes.bool,
    visible: PropTypes.bool,
    searchCenter: PropTypes.shape({
        latitude: PropTypes.number,
        longitude: PropTypes.number
    }),
    searchDistance: PropTypes.number,
    isGeoMode: PropTypes.bool,
    bounds: PropTypes.object
};